import { Fragment, useState, useEffect, useContext } from "react";
import { createUser, editUser, deleteUser } from "../../actions/manage";
import { ReducerContext } from "../../contexts/ReducerContext";
import { handleSubmitPromise } from "../../utils/handleSubmitPromise";
import { DefaultFooter, Input, LoadingBox, Select, SmartForm } from "../extras/SmartForm";
import { ConfirmForm } from "../extras/ConfirmForm";
import { CloseButton } from "../static/Buttons";
import { ErrorMessage, InfoMessage, SuccessMessage } from "../static/Messages";
import { getLongDate } from "../../utils/calendar";
import { ROLES } from "../../config/roles";

const instruments = ['Violín','Viola','Violoncello','Contrabajo','Piano','Arpa','Flauta','Oboe','Clarinete','Saxofón','Fagot','Trompa','Trompeta','Trombón','Bombardino','Tuba','Guitarra','Bajo','Percusión']

// ── CreateUser (sync modal) ──────────────────────────────────────────

export const CreateUser = {
    Header: () => (
        <Fragment><i className="fas fa-1x fa-user-plus"></i> Añadir Nuevo Usuario</Fragment>
    ),
    Body: ({ onClose, modalState }) => {
        const [,,dispatch, user] = useContext(ReducerContext)

        const handleSubmit = (data) => new Promise((resolve, reject) => {
            dispatch(createUser(user.token, data), resolve, reject)
        })

        return(
            <div className="uk-modal-body om-body">
                <UserForm onSubmit={handleSubmit} onClose={onClose} modalState={modalState} isCreate={true}>
                    <button className="uk-button uk-button-primary" type="submit"><i className="fa fa-plus"></i> <span className="hide-s">Añadir</span></button>
                </UserForm>
            </div>
        )
    }
}

// ── EditUser (sync modal — data from state.users) ───────────────────

export const EditUser = {
    Header: () => (
        <Fragment><i className="fas fa-user-edit"></i> Editar usuario</Fragment>
    ),
    Body: ({ onClose, modalState, id }) => {
        const [,state, dispatch, user] = useContext(ReducerContext)
        const userData = state.users?.find(u => u.user === id)

        const handleSubmit = (data) => new Promise((resolve, reject) => {
            const payload = { fullname: data.name, surnames: data.surnames, email: data.email, instrument: data.instrument }
            if(data.permissions !== undefined) payload.permissions = data.permissions
            dispatch(editUser(user.token, id, payload), resolve, reject)
        })

        if(!userData) return null

        return(
            <div className="uk-modal-body om-body">
                <UserForm user={userData} currentUser={user.username} onSubmit={handleSubmit} onClose={onClose} modalState={modalState}>
                    <button className="uk-button uk-button-primary" type="submit"><i className="fa fa-save"></i> <span className="hide-s">Guardar</span></button>
                </UserForm>
            </div>
        )
    }
}

// ── RemoveUser (sync modal) ─────────────────────────────────────────

export const RemoveUser = {
    Header: () => (
        <Fragment><i className="fas fa-user-minus"></i> Eliminar usuario</Fragment>
    ),
    Body: ({ onClose, modalState, id }) => {
        const [,,dispatch, user] = useContext(ReducerContext)

        const handleSubmit = () => new Promise((resolve, reject) => {
            dispatch(deleteUser(user.token, id), resolve, reject)
        })

        return(
            <div className="uk-modal-body">
                <div className="om-body">
                    <ConfirmForm onClose={onClose} onSubmit={handleSubmit} modalState={modalState} buttonStyle={"danger"} buttonText={"Eliminar"} buttonIcon={"fa-user-minus"}>
                        <p className="uk-text-justify uk-margin-remove-top">¿De verdad desea eliminar el usuario <b>{id}</b>? Esta acción no puede ser revertida. Se eliminarán todos sus datos de la agrupación.</p>
                    </ConfirmForm>
                </div>
            </div>
        )
    }
}

// ── UserDetails (async modal — loads user with missed) ──────────────

const permissionsLabel = (p) => { switch(p) { case 1: return 'Administrador'; case 2: return 'Moderador'; default: return 'Componente' } }

export const UserDetails = {
    Header: () => (
        <Fragment><i className="fas fa-1x fa-user-tag"></i> Información del usuario</Fragment>
    ),
    Body: ({ onClose, elem }) => {
        const [,state] = useContext(ReducerContext)
        const userFromList = state.users?.find(u => u.user === elem.user)

        return(
            <div className="uk-modal-body">
                <div className="om-body">
                    <CloseButton onClick={onClose}/>
                    <p className="uk-margin-remove-top"><i className="fas fa-file-signature"></i> <b>Apellidos, Nombre</b> <span style={{marginRight: '2.5%'}}>{elem.surnames}, {elem.name}</span><i className="fas fa-guitar"></i> <b>Instrumento</b> <span>{elem.instrument || '—'}</span></p>
                    <p className="uk-margin-remove-top"><i className="fas fa-mail-bulk"></i> <b>Correo Electrónico</b> <span style={{marginRight: '2.5%'}}>{elem.email}</span><i className="fas fa-info-circle"></i> <b>Estado</b> <span>{userFromList?.active ? <span>Activo</span> : <span>Inactivo</span>}</span></p>
                    <p className="uk-margin-remove-top"><i className="fas fa-key"></i> <b>Privilegios</b> <span style={{marginRight: '2.5%'}}>{permissionsLabel(userFromList?.permissions)}</span><i className="fas fa-phone" style={{transform: 'rotate(90deg)'}}></i> <b>Teléfono/s</b> <span>{elem.phone1 || 'No hay datos'}</span> | <span>{elem.phone2 || 'No hay datos'}</span></p>
                    <hr/>
                    <p className="uk-margin-remove-bottom"><i className="fas fa-user-times"></i> <b>Faltas de Asistencia</b></p>
                    <div className="uk-grid-small uk-grid" uk-grid="">
                        {elem.missed && elem.missed.length > 0 ?
                            [0, 1].map(col => (
                                <div key={col} className="uk-width-1-2">
                                    {elem.missed.filter((_, i) => i % 2 === col).map((m, i) => <div key={i}>{getLongDate(m.control)}</div>)}
                                </div>
                            ))
                        : 'No se han encontrado datos.'}
                    </div>
                </div>
            </div>
        )
    }
}

// ── UserForm (shared form for Create & Edit) ────────────────────────

const UserForm = ({ onClose, modalState, user: userData, currentUser, onSubmit = () => {}, isCreate = false, children }) => {
    const [fullname, setFullname] = useState("")
    const [email, setEmail] = useState("")
    const [instrument, setInstrument] = useState("Violín")
    const [permissions, setPermissions] = useState(3)
    const [date, setDate] = useState("")

    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)

    useEffect(() => {
        if(modalState) {
            setErr("");setLoading(false);setSuccess(false);
        }
    }, [modalState])

    useEffect(() => {
        if(userData && modalState) {
            setFullname(`${userData.surnames}, ${userData.name}`);
            setEmail(userData.email || "");setInstrument(userData.instrument || "Violín");
            setPermissions(userData.permissions || 3);
        } else if(modalState) {
            setFullname("");setEmail("");setInstrument("Violín");setPermissions(3);setDate("");
        }
    }, [userData, modalState])

    const disablePermissions = !isCreate && (userData?.permissions === ROLES.ADMIN || userData?.user === currentUser)

    const formValidation = (e) => {
        e.preventDefault()
        const parts = fullname.split(',')
        if(parts.length < 2 || !parts[0].trim() || !parts[1].trim()) {
            setErr('Formato requerido: Apellidos, Nombre'); return
        }
        if(!email) { setErr('Introduzca un correo electrónico'); return }
        const data = { name: parts[1].trim(), surnames: parts[0].trim(), email, instrument }
        if(!disablePermissions) data.permissions = Number(permissions)
        if(isCreate && date) data.date = date
        handleSubmitPromise(onSubmit(data), setErr, setSuccess, setLoading)
    }

    return(
        <SmartForm onSubmit={formValidation} states={[loading, err, success]}>
            <div className="uk-grid-small uk-grid uk-margin-medium" uk-grid="">
                <div className="uk-width-2-3">
                    <Input className="uk-input uk-margin-small" value={fullname} onChange={e => setFullname(e.target.value)} type="text" placeholder="Apellidos, Nombre" autoFocus/>
                </div>
                <div className="uk-width-1-3">
                    <Select className="uk-select" value={instrument} onChange={e => setInstrument(e.target.value)}>
                        {instruments.map(inst => <option key={inst} value={inst}>{inst}</option>)}
                    </Select>
                </div>
                <div className="uk-width-2-3">
                    <Input className="uk-input" value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Correo Electrónico"/>
                </div>
                <div className="uk-width-1-3">
                    <select className="uk-select" value={permissions} onChange={e => setPermissions(e.target.value)} disabled={disablePermissions}>
                        <option value={3}>Componente</option>
                        <option value={2}>Moderador</option>
                        <option value={1}>Administrador</option>
                    </select>
                </div>
                {isCreate && <Fragment>
                    <div className="uk-width-1-3">
                        <Input className="uk-input" value={date} onChange={e => setDate(e.target.value)} placeholder="DD/MM/YYYY"/>
                    </div>
                    <div className="uk-width-2-3">
                        <p className="uk-text-small uk-text-justify">Aquí se introducirá la fecha en la que el usuario expirará. Dejar en blanco si no es temporal.</p>
                    </div>
                </Fragment>}
            </div>
            {disablePermissions && <InfoMessage important info={'Los privilegios de este usuario no pueden ser modificados.'}/>}
            <ErrorMessage err={err}/>
            <SuccessMessage onHide={onClose} expand={false} success={success}>¡Guardado correctamente!</SuccessMessage>
            <LoadingBox expand={false}>
                <DefaultFooter cancelOnly={true} onClose={onClose}/>
                {children}
            </LoadingBox>
        </SmartForm>
    )
}
